/**
 * The one control, in the two dresses the CRM needs it in: a pill button for a
 * page head, and a row inside a `Pop` menu for a register.
 *
 * Both make the SAME decision - is my clock on this task, and does pressing this
 * start one or stop one - and neither owns what happens next. Starting while
 * another clock runs, and stopping one that has been going since yesterday, both
 * end in a question, and the question belongs to `TimerProvider` so that it
 * survives this menu closing. See `TimerDialogs`.
 *
 * Somebody else's clock on the same task is deliberately NOT in the way. Two
 * people can work on one thing at the same time, and a button that refused
 * because a colleague was already timing it would be inventing a rule the
 * business does not have. Their clock is drawn by `TimerMark` instead.
 */
import { Loader2, Play, Square } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { Btn } from "../ui";
import { ElapsedClock } from "./ElapsedClock";
import { useTimers } from "./TimerProvider";

/** My clock, if it is on this task. Null covers both "no clock" and "a clock on
 *  something else" - the button reads the same in either case, because in both
 *  of them pressing it means "start timing this". */
function useMineOn(taskId: string) {
  const timers = useTimers();
  const mine = timers?.mine ?? null;
  return mine && mine.taskId === taskId ? mine : null;
}

export function TimerButton({ taskId, small }: { taskId: string; small?: boolean }) {
  const t = useT();
  const timers = useTimers();
  const running = useMineOn(taskId);
  // Drawn for nobody who cannot press it. An operator without
  // `manage:crm.projects` never reaches the task register at all, so this is the
  // preview entry points and any future read-only surface.
  if (!timers?.enabled) return null;

  if (running) {
    return (
      <Btn
        tone="danger"
        small={small}
        onClick={() => void timers.requestStop(running)}
        disabled={timers.busy}
        /* Named even though the word is right there, because on a phone it is
           NOT: the label folds away below 640px and what is left is a red pill
           with four digits in it, which reads to a screen reader as "02:03". */
        title={t("timer.stop")}
        aria-label={t("timer.stop")}
      >
        {timers.busy ? <Loader2 className="size-4 animate-spin" /> : <Square className="size-4" />}
        <ElapsedClock timer={running} />
        <span className="max-sm:hidden">{t("timer.stop")}</span>
      </Btn>
    );
  }

  return (
    <Btn tone="dark" small={small} onClick={() => void timers.startOn(taskId)} disabled={timers.busy}>
      {timers.busy ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
      {t("timer.start")}
    </Btn>
  );
}

/**
 * The same control as a menu row.
 *
 * `onDone` closes the menu around it. It is called BEFORE the request rather
 * than after, because half of these end in a dialog and a menu still standing
 * open underneath one is a menu the operator has to dismiss twice.
 */
export function TimerMenuItem({ taskId, onDone }: { taskId: string; onDone: () => void }) {
  const t = useT();
  const timers = useTimers();
  const running = useMineOn(taskId);
  if (!timers?.enabled) return null;

  return (
    <button
      type="button"
      className={cn("crm-mi", running && "is-on")}
      disabled={timers.busy}
      onClick={() => {
        onDone();
        if (running) void timers.requestStop(running);
        else void timers.startOn(taskId);
      }}
    >
      {running ? <Square className="size-4" /> : <Play className="size-4" />}
      <span className="flex-1 text-left">{running ? t("timer.stop") : t("timer.start")}</span>
      {running ? <ElapsedClock timer={running} className="crm-mut text-[12.5px]" /> : null}
    </button>
  );
}
